import React, { useCallback, useEffect, useState } from 'react';
import type { Employee } from '../types/employee';
import {
  loadEmployees,
  repairEmployeesStorageIfNeeded,
  saveEmployees,
  subscribeToEmployeeUpdates,
} from '../services/employeeStorage';
import {
  createEmployee,
  updateEmployee,
  type CreateEmployeeInput,
  type UpdateEmployeeInput,
} from '../services/hr/employeeProfileService';
import { hashPin, isValidPin } from '../services/pinService';
import { isConvexConfigured, useConvexEmployees } from '../hooks/useConvexEmployees';

/**
 * Where employee records come from. WorkforceContext consumes this so the rest
 * of the app is unaware of whether data lives in Convex or localStorage.
 *
 * Convex is used whenever VITE_CONVEX_URL is set; the localStorage source
 * remains for local demo runs with no backend. The choice is made at the
 * component boundary rather than inside a hook, because the Convex hooks
 * require a ConvexProvider that does not exist in demo mode.
 */
export type EmployeeSource = {
  employees: Employee[];
  isLoading: boolean;
  refreshEmployees: () => void;
  createEmployeeProfile: (input: CreateEmployeeInput) => Promise<Employee>;
  updateEmployeeProfile: (input: UpdateEmployeeInput) => Promise<void>;
  setEmployeePin: (employeeId: string, pin: string) => Promise<boolean>;
};

function LocalEmployeeSource({
  children,
}: {
  children: (source: EmployeeSource) => React.ReactNode;
}) {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const loaded = loadEmployees();
    setEmployees(loaded);
    repairEmployeesStorageIfNeeded(loaded);
    setHydrated(true);
  }, []);

  useEffect(() => subscribeToEmployeeUpdates(() => setEmployees(loadEmployees())), []);

  const refreshEmployees = useCallback(() => setEmployees(loadEmployees()), []);

  const createEmployeeProfile = useCallback(async (input: CreateEmployeeInput) => {
    const created = createEmployee(input);
    setEmployees(loadEmployees());
    return created;
  }, []);

  const updateEmployeeProfile = useCallback(async (input: UpdateEmployeeInput) => {
    updateEmployee(input);
    setEmployees(loadEmployees());
  }, []);

  const setEmployeePin = useCallback(async (employeeId: string, pin: string) => {
    if (!isValidPin(pin)) return false;
    const next = loadEmployees().map((e) =>
      e.id === employeeId ? { ...e, pinHash: hashPin(pin) } : e
    );
    saveEmployees(next);
    setEmployees(next);
    return true;
  }, []);

  return (
    <>
      {children({
        employees,
        isLoading: !hydrated,
        refreshEmployees,
        createEmployeeProfile,
        updateEmployeeProfile,
        setEmployeePin,
      })}
    </>
  );
}

function ConvexEmployeeSource({
  children,
}: {
  children: (source: EmployeeSource) => React.ReactNode;
}) {
  const convexApi = useConvexEmployees();

  return (
    <>
      {children({
        employees: convexApi.employees,
        isLoading: convexApi.isLoading,
        // Convex queries are already reactive, so an explicit refresh is a no-op.
        refreshEmployees: () => {},
        createEmployeeProfile: convexApi.createEmployeeProfile,
        updateEmployeeProfile: convexApi.updateEmployeeProfile,
        setEmployeePin: convexApi.setEmployeePin,
      })}
    </>
  );
}

export function EmployeeSourceProvider({
  children,
}: {
  children: (source: EmployeeSource) => React.ReactNode;
}) {
  // SPA prerender runs React on the server. Convex hooks need ConvexProvider,
  // which may not be ready in that shell — use local source until mounted.
  const [clientReady, setClientReady] = useState(false);
  useEffect(() => setClientReady(true), []);

  const useConvex = clientReady && isConvexConfigured();
  const Source = useConvex ? ConvexEmployeeSource : LocalEmployeeSource;
  return <Source>{children}</Source>;
}
